import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { wantsTutor } from '@/features/tutor/client';
import type { StudentModel } from '@/features/tutor/types';
import { DzisView } from '@/nauka/DzisView';
import { findResumableFeed, localFeedCheckpoints, type ResumeFeed } from '@/nauka/feed-session';
import { flushLessonDraftWrites } from '@/nauka/lesson-draft';
import { FeedView, type Tryb } from '@/nauka/FeedView';
import { useNauka } from '@/nauka/useNauka';
import { PRACTICE_LEKCJE as LEKCJE, practiceLesson as lekcjaNauki } from '@/nauka/practice-course';
import { odlozonaTeraz, postep as postepNauki, powtorkaNaTeraz, wybierzTrening } from '@/nauka/silnik';
import { spojnyPostep } from '@/nauka/spojny-postep';
import { useSesja } from '@/nauka/sesja/useSesja';
import { SesjaView, type TrybSesji } from '@/nauka/sesja/SesjaView';
import { opanowanieTematu } from '@/nauka/sesja/model';
import { UMIEJETNOSCI_TEMATU } from '@/nauka/sesja/tresc';
import { CORPORA, SUBJECT_LABELS, useForge, type Screen, type SubjectId } from './useForge';
import { remainingMinutes, subjectGlance, useCourse } from './useCourse';
import { dayKey } from '@/learning-engine/schedule';
import { Shell } from './Shell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { MissionSummary } from '@/features/missions/MissionSummary';
import { Arena } from '@/features/questions/Arena';
import { MasteryMap } from '@/features/mastery-map/MasteryMap';
import { ErrorLab } from '@/features/error-lab/ErrorLab';
import { practiceFor, repairFor, sprawdzianDzialu, sprawdzianTytul, timeTrial, trainingFor } from '@/learning-engine/mission';
import { examLevelOf } from '@/learning-engine/course';
import { openErrorCount } from '@/learning-engine/error-lab';
import { DiagnosticIntro } from '@/features/diagnostics/DiagnosticIntro';
import { DiagnosticReportView } from '@/features/diagnostics/DiagnosticReportView';
import { WeeklyReportView } from '@/features/weekly-review/WeeklyReportView';
import { AiSettings } from '@/features/ai/AiSettings';
import { createTutor } from '@/features/ai/tutor';
import { DataScreen, type SubjectInfo } from '@/features/data/DataScreen';
import { TodayView } from '@/features/course/TodayView';
import { CourseView } from '@/features/course/CourseView';
import { LessonView } from '@/features/course/LessonView';
import { CalendarView } from '@/features/course/CalendarView';
import { ProgressView } from '@/features/course/ProgressView';
import { FlashcardsView } from '@/features/flashcards/FlashcardsView';
import { ExamsView } from '@/features/exams/ExamsView';
import { MATH_EXAMS } from '@content/exams/math-exams';
import { CS_EXAMS } from '@content/exams/cs-exams';
import type { ExamSheet } from '@content/exams/types';
import type { Skill } from '@/data/types';
import { MATH_CORPUS } from '@content/math/index';
import { CS_CORPUS } from '@content/cs/index';
import { BIZ_CORPUS } from '@content/biz/index';

/** Ekran danych działa na obu przedmiotach naraz, niezależnie od wybranego. */
const DATA_SUBJECTS: SubjectInfo[] = [
  { id: 'math', label: SUBJECT_LABELS.math, skillIds: MATH_CORPUS.skills.map((s) => s.id) },
  { id: 'cs', label: SUBJECT_LABELS.cs, skillIds: CS_CORPUS.skills.map((s) => s.id) },
  { id: 'biz', label: SUBJECT_LABELS.biz, skillIds: BIZ_CORPUS.skills.map((s) => s.id) },
];
/** Katalog oficjalnych arkuszy CKE według przedmiotu. */
/** Biznes i zarządzanie: pierwsza matura w maju 2027, arkuszy jeszcze nie ma. */
const EXAMS: Record<'math' | 'cs' | 'biz', ExamSheet[]> = { math: MATH_EXAMS, cs: CS_EXAMS, biz: [] };

const SUBJECT_IDS = Object.keys(CORPORA) as SubjectId[];

const ALL_SKILLS = [...MATH_CORPUS.skills, ...CS_CORPUS.skills, ...BIZ_CORPUS.skills];
const ALL_QUESTIONS = [...MATH_CORPUS.questions, ...CS_CORPUS.questions, ...BIZ_CORPUS.questions];
const TutorView = lazy(() => import('@/features/tutor/TutorView'));

export function App() {
  const forge = useForge();
  const { state, skills, questions, topics, corpus, beginMission, goTo, toCommandCenter } = forge;

  // AI jest opcjonalne i domyślnie wyłączone: włącza je dopiero klucz
  // podany w tej sesji (sek. 11). Tutor tworzymy raz na całe życie aplikacji.
  const tutor = useMemo(() => createTutor(), []);
  const [aiEnabled, setAiEnabled] = useState(false);
  useEffect(() => {
    if (tutor.unavailableReason) return;
    void tutor.keyPresent().then(setAiEnabled).catch(() => setAiEnabled(false));
  }, [tutor]);
  const catalogue = useMemo(() => questions.flatMap((q) => q.commonErrors), [questions]);

  const portRef = useRef(forge.storage);
  portRef.current = forge.storage;
  const port = useCallback(() => portRef.current(), []);
  const tutorLink = useRef(wantsTutor());
  const [startTutorNow, setStartTutorNow] = useState(false);
  const consumeTutorStart = useCallback(() => setStartTutorNow(false), []);
  useEffect(() => {
    if (tutorLink.current && state.screen !== 'loading') { tutorLink.current = false; goTo('tutor'); }
  }, [state.screen, goTo]);
  useEffect(() => {
    if (state.screen === 'tutor' && !location.hash.startsWith('#tutor-scan=')) history.replaceState(history.state, '', location.pathname + location.search + '#tutor');
    else if (state.screen !== 'loading' && location.hash === '#tutor' && !tutorLink.current) history.replaceState(history.state, '', location.pathname + location.search);
  }, [state.screen]);
  const tutorCursor = useRef('');
  const [tutorSyncError, setTutorSyncError] = useState('');
  const synchronizeTutor = useCallback((student: StudentModel) => {
    const last = student.appliedSubmissionIds.at(-1) ?? '';
    if (!last || last === tutorCursor.current) return;
    void (async () => {
      const storage = port(), existing = new Map((await storage.loadSkillStates()).map(s => [s.skillId, s]));
      for (const evidence of Object.values(student.skills)) {
        const before = existing.get(evidence.state.skillId);
        if ((evidence.state.lastAttemptAt ?? 0) > (before?.lastAttemptAt ?? 0)) await storage.saveSkillState(evidence.state);
      }
      tutorCursor.current = last; setTutorSyncError(''); await forge.reloadProfile();
    })().catch(() => setTutorSyncError('Profil tutora jest zapisany na serwerze. Nie udało się zaktualizować lokalnej mapy kursu.'));
  }, [port, forge.reloadProfile]);
  const { stan: stanNauki, zmien: zmienNauke } = useNauka(port, state.screen !== 'loading');
  const spojny = useMemo(
    () => spojnyPostep(stanNauki, state.skillStates, forge.lessonProgress),
    [stanNauki, state.skillStates, forge.lessonProgress],
  );

  // --- Nowy tryb nauki matematyki (demo): osobny klucz learning_progress_v2 ---
  const { postep: postepV2, zmien: zmienV2 } = useSesja(port, state.screen !== 'loading');
  const [trybSesji, setTrybSesji] = useState<TrybSesji>('lekcja');
  const nazwyMat = useMemo(() => Object.fromEntries(MATH_CORPUS.skills.map((x) => [x.id, x.name])), []);
  const otworzSesje = useCallback((tryb: TrybSesji = 'lekcja') => {
    setTrybSesji(tryb);
    goTo('sesja');
  }, [goTo]);
  // Bezpośredni link do demo: …/#sesja (np. do sprawdzenia na telefonie).
  const linkSesji = useRef(typeof location !== 'undefined' && location.hash === '#sesja');
  useEffect(() => {
    if (!linkSesji.current || state.screen === 'loading' || !postepV2) return;
    linkSesji.current = false;
    history.replaceState(history.state, '', location.pathname + location.search);
    otworzSesje('lekcja');
  }, [state.screen, postepV2, otworzSesje]);

  // Dzień ma jeden budżet na wszystkie przedmioty: każdy kalendarz wie, ile
  // materiału zostało w pozostałych.
  const day = dayKey(Date.now());
  const loads = useMemo(() => {
    const now = Date.now();
    return Object.fromEntries(
      SUBJECT_IDS.map((id) => [id, remainingMinutes(CORPORA[id], spojny.states, now)]),
    ) as Record<SubjectId, number>;
    // `day`: po północy "przerobione dziś" przestaje być dzisiejsze.
  }, [spojny.states, day]);
  const totalLoad = SUBJECT_IDS.reduce((sum, id) => sum + loads[id], 0);

  const course = useCourse({
    corpus,
    states: spojny.states,
    attempts: forge.attempts,
    lessonProgress: spojny.lessons,
    cardStates: forge.cardStates,
    dayMode: state.dayMode,
    deadline: forge.courseDeadline,
    otherMinutes: totalLoad - loads[state.subject],
  });

  const others = useMemo(
    () =>
      SUBJECT_IDS.filter((id) => id !== state.subject).map((id) => ({
        id,
        label: SUBJECT_LABELS[id],
        glance: subjectGlance(
          {
            corpus: CORPORA[id],
            states: spojny.states,
            attempts: forge.attempts,
            lessonProgress: spojny.lessons,
            cardStates: forge.cardStates,
            dayMode: state.dayMode,
            deadline: forge.courseDeadline,
            otherMinutes: totalLoad - loads[id],
          },
          Date.now(),
        ),
      })),
    [state.subject, spojny.states, state.dayMode, forge.attempts, spojny.lessons, forge.cardStates, forge.courseDeadline, loads, totalLoad],
  );

  // --- Prototyp nauki: feed kart dla sześciu lekcji próbki -------------------
  const [resumableFeed, setResumableFeed] = useState<ResumeFeed | null>(null);
  useEffect(() => {
    if (state.screen !== 'command-center' || !stanNauki) { setResumableFeed(null); return; }
    let live = true;
    void flushLessonDraftWrites().then(() => port().loadPreferences()).then(prefs => {
      if (live) setResumableFeed(findResumableFeed([...prefs, ...localFeedCheckpoints()], stanNauki, LEKCJE.filter(l => l.przedmiot === state.subject)));
    }).catch(() => {
      if (live) setResumableFeed(findResumableFeed(localFeedCheckpoints(), stanNauki, LEKCJE.filter(l => l.przedmiot === state.subject)));
    });
    return () => { live = false; };
  }, [state.screen, state.subject, stanNauki, port]);
  const [feed, setFeed] = useState<{ skillId: string; tryb: Tryb; returnToMap: boolean } | null>(null);
  const otworzFeed = (skillId: string, tryb: Tryb) => {
    const cel = tryb === 'trening' && stanNauki
      ? wybierzTrening(stanNauki, LEKCJE.filter((l) => l.przedmiot === state.subject), Date.now())
      : null;
    if (tryb === 'trening' && !cel) {
      const next = course.ordered.find((s) => course.lessonOf.has(s.id) && !course.lessonsDone.has(s.id));
      if (next) forge.openLesson(next.id);
      else toCommandCenter();
      return;
    }
    setFeed(previous => ({ skillId: cel?.skillId ?? skillId, tryb,
      returnToMap: state.screen === 'mastery-map' || (state.screen === 'nauka' && Boolean(previous?.returnToMap)),
    }));
    goTo('nauka');
  };
  /** Sześć lekcji próbki otwiera feed; pozostałe — dotychczasowy widok lekcji. */
  const otworzLekcje = (skillId: string) => {
    const l = lekcjaNauki(skillId);
    if (!l || !stanNauki) {
      forge.openLesson(skillId);
      return;
    }
    const s = postepNauki(stanNauki, l).status;
    otworzFeed(skillId, s === 'nowa' || s === 'w trakcie' ? 'nauka' : 'trening');
  };
  const practice = (skill: Skill) => {
    if (lekcjaNauki(skill.id)) otworzLekcje(skill.id);
    else beginMission(practiceFor(skill));
  };

  // Odłożona lekcja (seria błędów) czeka do jutra — w tym czasie idziemy dalej.
  const odlozona = (skillId: string) => Boolean(stanNauki && lekcjaNauki(skillId) && odlozonaTeraz(stanNauki, skillId, Date.now()));
  const nastepnaWKursie = (pomin?: string) =>
    course.ordered.find((s) => s.id !== pomin && course.lessonOf.has(s.id) && !course.lessonsDone.has(s.id) && !odlozona(s.id));

  // Prosty sprawdzian działu: gdy cała podstawa działu jest przerobiona, a sprawdzianu jeszcze nie było.
  const sprawdzian = (() => {
    for (const t of topics) {
      const pp = course.ordered.filter((s) => s.topicId === t.id && examLevelOf(s) === 'PP' && course.lessonOf.has(s.id));
      if (pp.length === 0 || !pp.every((s) => course.lessonsDone.has(s.id))) continue;
      if (forge.missions.some((m) => m.kind === 'boss' && m.finishedAt !== null && m.title === sprawdzianTytul(t))) continue;
      return { topic: t, skills: pp };
    }
    return null;
  })();

  if (state.screen === 'loading') {
    return <p className="boot">Wczytywanie profilu…</p>;
  }

  // --- Ekrany skupienia: bez nawigacji wokół (sek. 7.2) -----------------------
  if (state.screen === 'tutor') return <ErrorBoundary onHome={toCommandCenter}><Suspense fallback={<p className="boot">Wczytywanie AI Tutora…</p>}>
    {tutorSyncError && <p role="alert">{tutorSyncError}</p>}
    <TutorView initialStates={spojny.states} onStudent={synchronizeTutor} autoStart={startTutorNow} onConsumeStart={consumeTutorStart} onBack={toCommandCenter} />
  </Suspense></ErrorBoundary>;

  if (state.screen === 'sesja' && postepV2) {
    return (
      <ErrorBoundary onHome={toCommandCenter}>
        <SesjaView
          tryb={trybSesji}
          postep={postepV2}
          zmien={zmienV2}
          dawne={spojny.states}
          fiszki={MATH_CORPUS.flashcards}
          stanyFiszek={forge.cardStates}
          onOcenFiszke={(f, r) => {
            void forge.rateCard(f, r);
          }}
          nazwyUmiejetnosci={nazwyMat}
          onWyjdz={toCommandCenter}
          onTryb={setTrybSesji}
        />
      </ErrorBoundary>
    );
  }

  if (state.screen === 'nauka' && feed && stanNauki) {
    const l = lekcjaNauki(feed.skillId);
    if (l) {
      return (
        <ErrorBoundary onHome={toCommandCenter}>
        <FeedView
          key={`${feed.skillId}-${feed.tryb}`}
          storage={forge.storage}
          lekcja={l}
          tryb={feed.tryb}
          stan={stanNauki}
          zmien={zmienNauke}
          przedmiot={SUBJECT_LABELS[l.przedmiot]}
          onWyjdz={() => feed.returnToMap ? goTo('mastery-map') : toCommandCenter()}
          wyklad={(onBack) => {
            const lesson = corpus.lessons.find((x) => x.skillId === l.skillId);
            const skill = skills.find((x) => x.id === l.skillId);
            return lesson && skill ? <LessonView lesson={lesson} skill={skill}
              topic={topics.find((x) => x.id === skill.topicId)} onBack={onBack}
              onPractice={onBack} backLabel="Wróć do zadania" /> : <button onClick={onBack}>Wróć do zadania</button>;
          }}
          onInna={otworzFeed}
          treningDostepny={Boolean(wybierzTrening(stanNauki, LEKCJE.filter((x) => x.przedmiot === l.przedmiot), Date.now()))}
          onNastepna={() => {
            const next = nastepnaWKursie(l.skillId);
            if (next) otworzLekcje(next.id);
            else toCommandCenter();
          }}
        />
        </ErrorBoundary>
      );
    }
  }

  if (state.screen === 'arena' && state.current && state.plan) {
    return (
      <Arena
        sessionId={state.mission?.id ?? 'practice'}
        storage={forge.storage}
        draft={forge.draft}
        {...(spojny.states.get(state.current.skill.id) ? { skillState: spojny.states.get(state.current.skill.id)! } : {})}
        attempts={forge.attempts}
        onDraft={forge.setDraft}
        onPause={toCommandCenter}
        saveError={forge.sessionSaveError}
        mathematical={state.subject === 'math'}
        selection={state.current}
        step={state.step}
        total={state.plan.questionCount}
        feedback={state.feedback}
        onSubmit={(answer, hintLevel, confidence) => {
          void forge.submitAnswer(answer, hintLevel, confidence);
        }}
        onAdvance={() => {
          void forge.advance();
        }}
        deadlineAt={state.missionDeadline}
        onTimeUp={() => {
          void forge.finishMissionNow();
        }}
        running={state.running}
        ai={{
          tutor,
          enabled: aiEnabled,
          recentErrorIds: state.skillStates.get(state.current.skill.id)?.recentErrors ?? [],
          catalogue,
        }}
      />
    );
  }

  if (state.screen === 'summary') {
    return (
      <MissionSummary
        steps={state.steps}
        skills={skills}
        states={state.skillStates}
        missionsToday={state.missionsToday}
        // "Jeszcze jedna" wraca do planu dnia, a nie startuje misji
        // automatycznie - Blueprint sek. 3 i 14.
        onAgain={toCommandCenter}
        onFinish={toCommandCenter}
      />
    );
  }

  // --- Ekrany w powłoce ------------------------------------------------------

  const extras = [...state.options, timeTrial()].filter(
    (o, i, all) => all.findIndex((x) => x.title === o.title) === i,
  );

  let page: ReactNode;
  switch (state.screen) {
    case 'course':
      page = (
        <CourseView
          course={course}
          subjectName={corpus.subject.name}
          topics={topics}
          skills={skills}
          states={spojny.states}
          nauka={stanNauki}
          nextSkillId={(() => {
            const review = course.ordered.find((s) => {
              const l = lekcjaNauki(s.id);
              return l && stanNauki && powtorkaNaTeraz(stanNauki, l.skillId, Date.now());
            });
            const due = course.ordered.find((s) => {
              const l = lekcjaNauki(s.id);
              return l && stanNauki && postepNauki(stanNauki, l).status === 'w trakcie';
            });
            const fresh = course.ordered.find((s) => {
              const l = lekcjaNauki(s.id);
              return l && stanNauki && postepNauki(stanNauki, l).status === 'nowa';
            });
            return review?.id ?? due?.id ?? fresh?.id ?? course.next?.id ?? null;
          })()}
          onOpenLesson={otworzLekcje}
          onPractice={practice}
        />
      );
      break;

    case 'lesson': {
      const lesson = corpus.lessons.find((l) => l.skillId === forge.lessonSkillId);
      const skill = skills.find((s) => s.id === forge.lessonSkillId);
      page =
        lesson && skill ? (
          <LessonView
            lesson={lesson}
            skill={skill}
            topic={topics.find((t) => t.id === skill.topicId)}
            onPractice={() => {
              if (lekcjaNauki(skill.id)) otworzFeed(skill.id, 'nauka');
              else void forge.finishLesson(skill.id);
            }}
            onBack={() => (lekcjaNauki(skill.id) && feed?.skillId === skill.id ? goTo('nauka') : goTo('course'))}
          />
        ) : (
          <p className="page">Tej lekcji nie ma w wybranym przedmiocie.</p>
        );
      break;
    }

    case 'calendar':
      page = (
        <CalendarView
          course={course}
          skills={skills}
          deadline={forge.courseDeadline}
          examDate={forge.examDate}
          dayMode={state.dayMode}
          onDeadline={(k) => {
            void forge.setCourseDeadline(k);
          }}
          onExamDate={(k) => {
            void forge.setExamDate(k);
          }}
          onOpenLesson={otworzLekcje}
        />
      );
      break;

    case 'progress':
      page = (
        <ProgressView
          course={course}
          skills={skills}
          states={spojny.states}
          attempts={forge.attempts}
          onPractice={practice}
        />
      );
      break;

    case 'flashcards':
      page = (
        <FlashcardsView
          queue={course.cardSession.queue}
          dueCount={course.cardSession.dueCount}
          newCount={course.cardSession.newCount}
          cards={course.cards}
          states={forge.cardStates}
          skills={skills}
          unlockedSkillIds={course.unlocked}
          onRate={(card, rating) => {
            void forge.rateCard(card, rating);
          }}
          onDone={toCommandCenter}
          nextLessonName={course.next?.name ?? null}
        />
      );
      break;

    case 'mastery-map':
      page = (
        <MasteryMap
          skills={skills}
          topics={topics}
          states={spojny.states}
          // Kliknięcie w węzeł uruchamia trening, nie otwiera statystyk (sek. 7.3).
          onSelect={(skill) => lekcjaNauki(skill.id)
            ? otworzLekcje(skill.id)
            : beginMission(trainingFor(skill, state.skillStates.get(skill.id)?.level ?? 0))}
          onBack={toCommandCenter}
        />
      );
      break;

    case 'diagnostic-intro':
      page = (
        <DiagnosticIntro
          subjectName={SUBJECT_LABELS[state.subject]}
          probeCount={forge.diagnosticSize}
          hasPreviousPlan={state.savedPlan !== null}
          onStart={forge.startDiagnostic}
          onBack={toCommandCenter}
        />
      );
      break;

    case 'diagnostic-report':
      page = state.report ? (
        <DiagnosticReportView
          report={state.report}
          preview={forge.previewPlan}
          onChoose={(variant, deadline) => {
            void forge.choosePlan(variant, deadline);
          }}
          onBack={toCommandCenter}
        />
      ) : null;
      break;

    case 'ai-settings':
      page = <AiSettings tutor={tutor} onChange={setAiEnabled} onBack={toCommandCenter} />;
      break;

    case 'data':
      page = (
        <DataScreen
          storage={forge.storage}
          subjects={DATA_SUBJECTS}
          skills={ALL_SKILLS}
          questions={ALL_QUESTIONS}
          onChanged={forge.reloadProfile}
          onBack={toCommandCenter}
        />
      );
      break;

    case 'weekly-report':
      page = <WeeklyReportView report={state.weekly} rhythm={state.rhythm} onBack={toCommandCenter}
        feedProgress={LEKCJE.filter((l) => l.przedmiot === state.subject && (stanNauki?.lekcje[l.skillId]?.ukonczona ?? 0) >= Date.now() - 7 * 86_400_000)
          .map((l) => ({ skillId: l.skillId, name: l.tytul, status: postepNauki(stanNauki!, l).status }))} />;
      break;

    case 'exams':
      page = (
        <ExamsView
          exams={EXAMS[state.subject]}
          results={forge.examResults.filter((r) => r.subjectId === state.subject)}
          subjectId={state.subject}
          skills={skills}
          topics={topics}
          hasLesson={(id) => course.lessonOf.has(id)}
          onSave={forge.saveExam}
          onDelete={forge.deleteExam}
          onPractice={practice}
          onOpenLesson={otworzLekcje}
        />
      );
      break;

    case 'error-lab':
      page = (
        <ErrorLab
          groups={state.errorGroups}
          skills={skills}
          onRepair={(skill, cause) => beginMission(repairFor(skill, cause))}
          onBack={toCommandCenter}
        />
      );
      break;

    case 'command-center':
      page = (
        <DzisView
          resume={resumableFeed && LEKCJE.some(l => l.skillId === resumableFeed.skillId && l.przedmiot === state.subject) ? { title: resumableFeed.title, onResume: () => {
            setFeed({ skillId: resumableFeed.skillId, tryb: resumableFeed.tryb, returnToMap: false });
            goTo('nauka');
          } } : null}
          przedmiot={state.subject}
          przedmiotNazwa={SUBJECT_LABELS[state.subject]}
          stan={stanNauki}
          onStart={otworzFeed}
          onWiecej={() => goTo('plan')}
          onKurs={() => goTo('course')}
          onTutor={() => { setStartTutorNow(true); goTo('tutor'); }}
          nextCourse={course.ordered.find((s) => !lekcjaNauki(s.id) && course.lessonOf.has(s.id) && !course.lessonsDone.has(s.id)) ?? null}
          sprawdzian={sprawdzian ? { nazwa: sprawdzian.topic.name, onStart: () => beginMission(sprawdzianDzialu(sprawdzian.topic, sprawdzian.skills)) } : null}
          onCourseLesson={forge.openLesson}
          descriptions={Object.fromEntries(corpus.lessons.map(l => [l.skillId, l.intro]))}
          sesja={state.subject === 'math' && postepV2 ? {
            temat: 'Funkcja kwadratowa',
            opanowanie: Math.round(opanowanieTematu(postepV2, UMIEJETNOSCI_TEMATU, spojny.states)),
            wToku: Boolean(postepV2.sesja && postepV2.sesja.koniec === null),
            onStart: () => otworzSesje('lekcja'),
          } : null}
        />
      );
      break;

    default:
      page = (
        <TodayView
          course={course}
          topics={topics}
          recommended={state.recommended}
          options={extras}
          rhythm={state.rhythm}
          dayMode={state.dayMode}
          deadline={forge.courseDeadline}
          examDate={forge.examDate}
          comeback={state.daily.comeback}
          onDayMode={(mode) => {
            void forge.setDayMode(mode);
          }}
          onStartMission={beginMission}
          onOpenLesson={otworzLekcje}
          onPractice={practice}
          onOpenFlashcards={() => goTo('flashcards')}
          onOpenCalendar={() => goTo('calendar')}
          onOpenCourse={() => goTo('course')}
          others={others}
          onSwitchSubject={(id) => {
            void forge.setSubject(id);
          }}
          diagnostic={{
            hasReport: state.report !== null && state.savedPlan === null,
            onOpen: () =>
              goTo(state.report !== null && state.savedPlan === null ? 'diagnostic-report' : 'diagnostic-intro'),
          }}
        />
      );
  }

  const badges: Partial<Record<Screen, number>> = {
    flashcards: course.cardSession.queue.length,
    'error-lab': openErrorCount(state.errorGroups),
  };

  const statTabs: { screen: Screen; label: string }[] = [
    { screen: 'plan', label: 'Plan' },
    { screen: 'progress', label: 'Postęp' },
    { screen: 'mastery-map', label: 'Mapa' },
    { screen: 'weekly-report', label: 'Raport' },
    { screen: 'error-lab', label: 'Błędy' },
  ];
  const inStats = statTabs.some((t) => t.screen === state.screen);

  return (
    <Shell
      screen={state.screen}
      subject={state.subject}
      onSubject={(next) => {
        void forge.setSubject(next);
      }}
      onNavigate={goTo}
      badges={badges}
    >
      <ErrorBoundary key={state.screen} onHome={toCommandCenter}>
        {inStats && <nav className="tabs" aria-label="Zakładki statystyk">
          {statTabs.map((t) => <button key={t.screen} type="button" className={state.screen === t.screen ? 'tabs__item tabs__item--on' : 'tabs__item'} aria-current={state.screen === t.screen ? 'page' : undefined} onClick={() => goTo(t.screen)}>{t.label}</button>)}
        </nav>}
        {state.mission?.finishedAt === null && state.current && <aside className="card" aria-label="Przerwana sesja">
          <p>{state.mission.title} · pytanie {state.step} z {state.plan?.questionCount}. Twoje odpowiedzi i rachunki czekają.</p>
          <button type="button" className="btn btn--primary" onClick={forge.resumeMission}>Wznów przerwaną sesję</button>
        </aside>}
        {page}
      </ErrorBoundary>
    </Shell>
  );
}
